import { Controller } from '../utils/controller';

export const COMPLETE_ATTRIBUTE = 'data-image-complete';
export const CACHED_ATTRIBUTE = 'data-image-cached';

/**
 * Provides information about the state of a lazy loaded image.
 * Appends attributes marking the state of the image loading process.
 *
 * With this, users can smoothly animate the lazy image appearing.
 * Use the transition between `data-image-complete="false"` to `data-image-complete="true"` to
 * animate a lazy loading image.
 * If the image is already cached then use `data-image-cached="true"` to cancel the animation.
 *
 * You can override the attributes for the `loading` and `cached` state.
 */
export default class extends Controller {
  static targets: string[] = ['image'];
  static requiredTargets = [];

  static values: {
    completeAttribute: string;
    cachedAttribute: string;
  };

  // TARGETS
  declare imageTargets: HTMLImageElement[];

  // VALUES
  declare completeAttributeValue: string;
  declare cachedAttributeValue: string;

  initialize() {
    const completeAttribute = this.completeAttribute;
    const cachedAttribute = this.cachedAttribute;

    this.imageTargets.forEach((imgElement) => {
      try {
        if (imgElement.complete) {
          imgElement.setAttribute(cachedAttribute, 'true');
          imgElement.setAttribute(completeAttribute, 'true');
        } else {
          let loadStart = 0;
          let loaded = false;

          // start monitoring loading - give the browser a chance to retrieve the image from cache
          // before assuming it's fetching from network
          // we're transitioning only from [data-image-complete="false"] to [data-image-complete="true"]
          setTimeout(() => {
            if (!loaded) {
              imgElement.setAttribute(completeAttribute, 'false');
            }
          }, 20);

          const markCached = () => {
            imgElement.setAttribute(cachedAttribute, 'true');
          };

          const markLoaded = () => {
            imgElement.setAttribute(completeAttribute, 'true');
          };

          // not all browsers implement the "loadstart" eventso we use an intersection observer
          // to determine the most likely situation for the browser to start loading the image
          imgElement.addEventListener('loadstart', () => {
            loadStart = new Date().getTime();
          });

          const observer = new IntersectionObserver(() => {
            if (imgElement.clientWidth && loadStart === 0) {
              loadStart = new Date().getTime();

              if (imgElement.complete) {
                // already cached
                markLoaded();
                markCached();
              } else if (window.requestAnimationFrame) {
                // try again
                window.requestAnimationFrame(() => {
                  if (imgElement.complete) {
                    markLoaded();
                    markCached();
                  }
                });
              }

              // we can disconnect since we already set the data
              observer.disconnect();
            }
          });
          observer.observe(imgElement);

          imgElement.addEventListener('load', () => {
            const loadEnd = new Date().getTime();
            const loadDuration = loadEnd - loadStart;

            markLoaded();
            if (loadDuration < 30) {
              markCached();
            }
          });

          // ensure cleanup
          const cleanup = () => {
            loaded = true;
            observer.disconnect();
          };
          imgElement.addEventListener('load', cleanup);
          imgElement.addEventListener('error', cleanup);
        }
      } catch (err) {
        // ignore
      }
    });
  }

  private get completeAttribute() {
    return this.completeAttributeValue ?? COMPLETE_ATTRIBUTE;
  }

  private get cachedAttribute() {
    return this.cachedAttributeValue ?? CACHED_ATTRIBUTE;
  }
}
